import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
  HiOutlineArrowUturnLeft,
  HiOutlineAtSymbol,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi2";
import { isApiClientError, isValidationApiError } from "../api/errors";
import { openRealtimeSubscription } from "../realtime";
import type { DocumentWorkflowDocument } from "../entities/document-workflow";
import { DocumentAttachments } from "./DocumentAttachments";
import { DocumentMailThread } from "./DocumentMailThread";
import { DocumentOverview } from "./DocumentOverview";
import { SectionSwitcher } from "./SectionSwitcher";
import { CloseButton } from "./CloseButton";
import {
  AttachmentPicker,
  type AttachmentPickerHandle,
  type PickedAttachment,
} from "./AttachmentPicker";
import {
  priorityLabels,
  statusLabels,
  type Priority,
} from "./documentWorkflowSearch";
import type { ShellServices } from "./services";
import { StoredFileAttachment } from "./StoredFileAttachment";
import { useDebouncedValue } from "./useDebouncedValue";
import { Select } from "./Select";
import { Notice } from "./Notice";
import { ApprovalStatusBadge } from "./ApprovalTab";
import { approvalTimelineTitle } from "./approvalTimeline";

type Tab =
  | "overview"
  | "details"
  | "mail"
  | "discussion"
  | "files"
  | "approval"
  | "timeline";
type DetailSection = "main" | "execution" | "access" | "history";
type Action = "start" | "complete" | "reopen" | "cancel";
const tabs: { id: Tab; label: string }[] = [
  { id: "overview", label: "Обзор" },
  { id: "details", label: "Реквизиты" },
  { id: "mail", label: "Внешняя переписка" },
  { id: "discussion", label: "Обсуждение" },
  { id: "files", label: "Файлы" },
  { id: "approval", label: "Согласование" },
  { id: "timeline", label: "История" },
];
const detailSections: { id: DetailSection; label: string; hint: string }[] = [
  { id: "main", label: "Основное", hint: "Реквизиты и регистрация" },
  { id: "execution", label: "Исполнение", hint: "Назначение и контроль" },
  { id: "access", label: "Доступ", hint: "Люди и КПО" },
  { id: "history", label: "Связи и экспорт", hint: "Аудит документа" },
];
const actions: Record<Action, string> = {
  start: "Начать",
  complete: "Завершить",
  reopen: "Переоткрыть",
  cancel: "Отменить",
};
const relationLabels = {
  related: "Связан",
  parent: "Родительский документ",
  child: "Дочерний документ",
  duplicate: "Дубликат",
  response_to: "Ответ на документ",
  answered_by: "Ответный документ",
} as const;
const timelineEventLabels: Record<string, string> = {
  "document.created": "Документ создан",
  "document.registered": "Документ зарегистрирован",
  "document.status_changed": "Статус документа изменён",
  "document.assigned": "Назначен исполнитель",
  "document.control_set": "Документ поставлен на контроль",
  "document.priority_changed": "Приоритет изменён",
  "document.read": "Документ прочитан",
  "document.scope_granted": "Открыт доступ организации",
  "document.relation_added": "Добавлена связь с документом",
  "document.history_exported": "Запрошена выгрузка истории",
  "mail.received": "Получено письмо",
  "mail.sent": "Отправлено письмо",
  "mail.outbound_queued": "Письмо поставлено в очередь",
  "comment.added": "Добавлено сообщение",
  "comment.edited": "Сообщение изменено",
  "comment.deleted": "Сообщение удалено",
  "comment.attachment_added": "К сообщению добавлен файл",
  "file.added": "Добавлен файл",
  "file.deleted": "Удалён файл",
  "file.version_added": "Добавлена версия файла",
  "file.signed": "Файл подписан",
};
const timelineFieldLabels: Record<string, string> = {
  subject: "Тема",
  type: "Тип документа",
  internal_kind: "Вид документа",
  priority: "Приоритет",
  received_at: "Дата получения",
  registration_number: "Регистрационный номер",
  registration_date: "Дата регистрации",
  incoming_number: "Входящий номер",
  copies_count: "Количество экземпляров",
  sheets_count: "Количество листов",
  journal: "Журнал",
  from_status: "Предыдущий статус",
  to_status: "Новый статус",
  assignee_name: "Исполнитель",
  controller_name: "Контролёр",
  due_at: "Срок",
  reason: "Причина",
  outcome: "Результат",
  display_name: "Файл",
  version_number: "Версия",
  comment: "Комментарий",
  format: "Формат",
  status: "Статус",
};
const reasons: Record<string, string> = {
  owner: "Создатель документа",
  assignee: "Исполнитель",
  controller: "Контролёр",
  recipient: "Получатель",
  participant: "Участник",
  scope_share: "Явный доступ КПО",
  organization_wide: "Доступ по membership и правам организации",
  approver: "Согласующий документа",
};
const roleLabels: Record<string, string> = {
  owner: "Создатель",
  assignee: "Исполнитель",
  controller: "Контролёр",
  recipient: "Получатель",
  participant: "Участник",
};
const removableRoleLabels: Record<string, string> = {
  assignee: "исполнителя",
  controller: "контролёра",
  recipient: "получателя",
  participant: "участника",
};
/** Число со склонением: «1 получатель», «2 получателя», «5 получателей». */
function plural(count: number, one: string, few: string, many: string) {
  const rest10 = count % 10;
  const rest100 = count % 100;
  const word =
    rest10 === 1 && rest100 !== 11
      ? one
      : rest10 >= 2 && rest10 <= 4 && (rest100 < 12 || rest100 > 14)
        ? few
        : many;
  return `${count} ${word}`;
}
function date(value: string | null | undefined) {
  return !value || Number.isNaN(Date.parse(value))
    ? "—"
    : new Intl.DateTimeFormat("ru-RU", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
}
function timelineValue(key: string, value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (
    (key.endsWith("_at") || key.endsWith("_date")) &&
    (typeof value === "string" || typeof value === "number")
  ) {
    return date(`${value}`);
  }
  if (
    (key === "priority" || key.endsWith("_priority")) &&
    typeof value === "string"
  ) {
    return priorityLabels[value as Priority] ?? value;
  }
  if (
    (key === "status" || key.endsWith("_status")) &&
    typeof value === "string"
  ) {
    return statusLabels[value as keyof typeof statusLabels] ?? value;
  }
  if (typeof value === "object") return JSON.stringify(value);
  if (["string", "number", "boolean", "bigint"].includes(typeof value)) {
    return `${value as string | number | boolean | bigint}`;
  }
  return "—";
}
function localDate(value: string | null) {
  if (!value) return "";
  const d = new Date(value);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}
function errorText(error: unknown) {
  if (isValidationApiError(error)) return "Проверьте отмеченные поля.";
  if (isApiClientError(error)) {
    if (error.status === 403)
      return "Раздел или действие недоступны: недостаточно прав.";
    if (error.status === 404) return "Документ или данные недоступны.";
    if (error.status === 409)
      return "Конфликт: данные изменились или переход недопустим. Карточка обновлена; проверьте её перед повтором.";
  }
  return "Не удалось загрузить или сохранить данные. Повторите попытку.";
}
function fieldErrorText(message: string) {
  return message === "This document relation already exists."
    ? "Такая связь с документом уже добавлена."
    : message;
}
function Section({
  title,
  query,
  children,
  className = "",
}: {
  title: string;
  query: {
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
  };
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`dw-panel${className ? ` ${className}` : ""}`}>
      <h3>{title}</h3>
      {query.isPending ? (
        <Notice role="status">Загрузка…</Notice>
      ) : query.isError ? (
        <>
          <Notice>{errorText(query.error)}</Notice>
          <button type="button" onClick={() => void query.refetch()}>
            Повторить загрузку
          </button>
        </>
      ) : (
        children
      )}
    </section>
  );
}

export function DocumentCard({
  document: doc,
  services,
  onClose,
  initialTab = "details",
  targetId = "",
  onTabChange,
}: {
  document: DocumentWorkflowDocument;
  services: ShellServices;
  onClose: () => void;
  initialTab?: Tab;
  targetId?: string;
  /** `target` — письмо или сообщение, к которому перешли из другого раздела. */
  onTabChange?: (tab: Tab, target?: string) => void;
}) {
  const client = useQueryClient();
  const api = services.documentWorkflow;
  const [tab, setTab] = useState<Tab>(initialTab);
  // Письмо или сообщение, на котором карточка стоит сейчас: из адреса либо
  // после перехода «Показать в переписке / в обсуждении» из файлов.
  const [focusId, setFocusId] = useState(targetId);
  useEffect(() => setFocusId(targetId), [targetId]);
  const [detailSection, setDetailSection] = useState<DetailSection>("main");
  const dialog = useRef<HTMLElement>(null);
  // Ленты вкладок на узком экране прокручиваются по горизонтали: активная
  // вкладка (в том числе открытая по ссылке или после перехода из файлов) не
  // должна оставаться за краем. Прокручивается только сама лента, страница и
  // карточка не сдвигаются.
  useEffect(() => {
    const nav = tabsNav.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active) return;
    nav.scrollTo?.({
      left: active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2,
    });
  }, [tab]);
  const [subject, setSubject] = useState(doc.subject);
  const [description, setDescription] = useState(doc.description ?? "");
  const [priority, setPriority] = useState<Priority>(doc.priority);
  const [incoming, setIncoming] = useState(doc.incoming_number ?? "");
  const [documentDate, setDocumentDate] = useState(doc.document_date ?? "");
  const [copies, setCopies] = useState(
    doc.copies_count == null ? "" : String(doc.copies_count),
  );
  const [sheets, setSheets] = useState(doc.sheets_count ?? "");
  const [manualNumber, setManualNumber] = useState("");
  const [received, setReceived] = useState(localDate(doc.received_at));
  const [registrationDate, setRegistrationDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [journal, setJournal] = useState("");
  const [assignee, setAssignee] = useState(doc.assignee_id ?? "");
  const [controller, setController] = useState(doc.controller_id ?? "");
  const [due, setDue] = useState(localDate(doc.due_at));
  const [reason, setReason] = useState("");
  const [outcome, setOutcome] = useState("");
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [override, setOverride] = useState(false);
  const [recipientIds, setRecipientIds] = useState<string[]>(
    doc.recipient_ids ?? [],
  );
  const [recipientId, setRecipientId] = useState("");
  const [participantId, setParticipantId] = useState("");
  const [scopeId, setScopeId] = useState("");
  const [level, setLevel] = useState<"read" | "write" | "manage">("read");
  const [relatedId, setRelatedId] = useState("");
  const [relationSearch, setRelationSearch] = useState("");
  const debouncedRelationSearch = useDebouncedValue(relationSearch.trim());
  const [relationSearchOpen, setRelationSearchOpen] = useState(false);
  const relationListId = useId();
  const [relationType, setRelationType] =
    useState<keyof typeof relationLabels>("related");
  const [comment, setComment] = useState("");
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [mentionSearch, setMentionSearch] = useState("");
  const [mentionOpen, setMentionOpen] = useState(false);
  const mentionPanelId = useId();
  const mentionButton = useRef<HTMLButtonElement>(null);
  const mentionSearchInput = useRef<HTMLInputElement>(null);
  const [commentAttachments, setCommentAttachments] = useState<
    readonly PickedAttachment[]
  >([]);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  // Сообщение, на которое пишется ответ: обсуждение читают как переписку, и без
  // этого непонятно, кому адресована реплика.
  const [replyToId, setReplyToId] = useState<string | null>(null);
  const [editedComment, setEditedComment] = useState("");
  const [editedMentionIds, setEditedMentionIds] = useState<string[]>([]);
  const [hasLiveUpdate, setHasLiveUpdate] = useState(false);
  // Первое непрочитанное сообщение на момент открытия обсуждения: по нему
  // рисуется линия «Новые сообщения» и считается, что ещё не прочитано.
  const [unreadAnchor, setUnreadAnchor] = useState<string | null>(null);
  // Лента сообщений: по ней следят, какие новые сообщения человек уже увидел.
  const conversationList = useRef<HTMLUListElement>(null);
  // Докуда уже сказали серверу «прочитано»: повторно то же сообщение не шлём.
  const reportedRead = useRef<string | null>(null);
  // Полоса вкладок: на узком экране она шире экрана и прокручивается.
  const tabsNav = useRef<HTMLElement>(null);
  // Файлы в сообщение можно вставить (Ctrl+V) или перетащить из папки.
  const commentPicker = useRef<AttachmentPickerHandle>(null);
  const [draggingFiles, setDraggingFiles] = useState(false);
  const dragDepth = useRef(0);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportFormat, setExportFormat] = useState<"json" | "csv">("json");
  useEffect(() => {
    if (mentionOpen) mentionSearchInput.current?.focus();
  }, [mentionOpen]);
  const can = (name: string) => doc.capabilities[`can_${name}`] === true;
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["document-workflow"] }),
      client.invalidateQueries({ queryKey: ["notifications"] }),
    ]);
  };
  const mutation = useMutation({
    mutationFn: async (job: () => Promise<unknown>) => job(),
    onSuccess: refresh,
    onError: async (error) => {
      if (isApiClientError(error) && [403, 404, 409].includes(error.status))
        await refresh();
    },
  });
  const run = (job: () => Promise<unknown>) => mutation.mutate(job);
  /**
   * Действия внутри обсуждения.
   *
   * Лента обновляется немедленно, а сводные данные — отдельными точечными
   * запросами, чтобы карточка не выглядела перезагруженной после реплики.
   */
  const discussion = useMutation({
    mutationFn: async (job: () => Promise<unknown>) => job(),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["document-workflow", "documents", doc.id, "comments"],
        }),
        client.invalidateQueries({
          queryKey: ["document-workflow", "registry"],
        }),
        client.invalidateQueries({
          queryKey: ["document-workflow", "dashboard"],
        }),
      ]);
    },
  });
  const say = (job: () => Promise<unknown>) => discussion.mutate(job);
  /**
   * Тихая отметка прочтения по мере чтения ленты.
   *
   * Отдельно от остальных действий: человек ничего не нажимал, поэтому ни
   * блокировок, ни сообщений об ошибке здесь быть не должно. Не дошедшая
   * отметка не теряется — следующее сообщение на экране отправит курсор
   * заново, а при открытии документа состояние всё равно перечитывается.
   */
  const markRead = useMutation({
    mutationFn: (commentId: string) => api.readDiscussion(doc.id, commentId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["document-workflow", "registry"],
        }),
        client.invalidateQueries({
          queryKey: ["document-workflow", "dashboard"],
        }),
        client.invalidateQueries({ queryKey: ["notifications"] }),
      ]);
    },
    onError: () => {
      reportedRead.current = null;
    },
  });
  const field = (name: string) => {
    const failure = [mutation.error, discussion.error].find(
      (error) => isValidationApiError(error) && error.details.fields[name],
    );
    if (!isValidationApiError(failure)) return null;
    const messages = failure.details.fields[name];
    if (messages === undefined) return null;

    return (
      <small className="field-error" role="alert">
        {messages.map(fieldErrorText).join(" ")}
      </small>
    );
  };
  const pending = mutation.isPending;
  const key = ["document-workflow", "documents", doc.id];
  const participants = useQuery({
    queryKey: [...key, "participants"],
    queryFn: () => api.participants(doc.id),
    retry: false,
  });
  const scopes = useQuery({
    queryKey: [...key, "scopes"],
    queryFn: () => api.scopes(doc.id),
    retry: false,
  });
  const participantCandidates = useQuery({
    queryKey: [...key, "participant-candidates"],
    queryFn: () => api.participantCandidates(doc.id),
    enabled: (can("manage_participants") || can("assign") || can("control")) && tab === "details",
    retry: false,
  });
  // Исполнителя и контролёра назначают из подчинённых по линейной иерархии,
  // а администратор назначает любого: круг определяет сервер, здесь он только
  // подсказка, чтобы не предлагать сотрудников, которых сервер отклонит.
  const delegation = useQuery({
    queryKey: ["hierarchy", "reach"],
    queryFn: () => services.administration.hierarchyReach(),
    enabled: (can("assign") || can("control")) && tab === "details",
    retry: false,
    staleTime: 60_000,
  });
  const allScopes = useQuery({
    queryKey: [...key, "allowed-scopes"],
    queryFn: () => services.administration.listScopes(),
    enabled: can("manage_scopes"),
    retry: false,
  });
  const assignments = useQuery({
    queryKey: [...key, "assignments"],
    queryFn: () => api.assignments(doc.id),
    enabled: tab === "details",
    retry: false,
  });
  const reads = useQuery({
    queryKey: [...key, "reads"],
    queryFn: () => api.reads(doc.id),
    enabled: tab === "details",
    retry: false,
  });
  const relations = useQuery({
    queryKey: [...key, "relations"],
    queryFn: () => api.relations(doc.id),
    enabled: tab === "details",
    retry: false,
  });
  const relationCandidates = useQuery({
    queryKey: [
      "document-workflow",
      "relation-candidates",
      debouncedRelationSearch,
    ],
    queryFn: () =>
      api.listDocuments({
        search: debouncedRelationSearch,
        page: 1,
        per_page: 8,
        sort: "-received_at",
      }),
    enabled:
      tab === "details" &&
      detailSection === "history" &&
      debouncedRelationSearch.length >= 2,
    retry: false,
  });
  const comments = useQuery({
    queryKey: [...key, "comments"],
    queryFn: () => api.comments(doc.id),
    enabled:
      (tab === "discussion" || tab === "files" || tab === "overview") &&
      can("view_discussion"),
    retry: false,
  });
  const currentUser = useQuery({
    queryKey: ["identity", "current-user"],
    queryFn: () => services.identity.currentUser(),
    enabled: tab === "discussion" && can("view_discussion"),
    retry: false,
  });
  const timeline = useInfiniteQuery({
    queryKey: [...key, "timeline-pages"],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.timelinePage(doc.id, pageParam),
    getNextPageParam: (last) => last.meta.next_cursor ?? undefined,
    enabled: tab === "timeline",
    retry: false,
  });
  const exported = useQuery({
    queryKey: [...key, "export", exportId],
    queryFn: () => api.historyExportStatus(exportId!),
    enabled: exportId !== null,
    retry: false,
    refetchInterval: (q) =>
      ["queued", "processing"].includes(q.state.data?.status ?? "")
        ? 1500
        : false,
  });
  const people = participants.data ?? [];
  const candidateById = new Map(
    (participantCandidates.data ?? []).map((user) => [user.id, user]),
  );
  const participantById = new Map(
    people.flatMap((participant) =>
      participant.user && participant.user_id
        ? [[participant.user_id, participant.user] as const]
        : [],
    ),
  );
  /**
   * Человека называют по имени.
   *
   * Пока карточки участников едут с сервера, показывается многоточие, а не
   * идентификатор: UUID в графе «Исполнитель» человеку ничего не говорит и
   * выглядит поломкой, хотя это всего лишь незавершённая загрузка.
   */
  const name = (id: string | null | undefined) =>
    !id
      ? "не назначен"
      : (currentUser.data?.id === id ? currentUser.data.display_name : null) ||
        candidateById.get(id)?.display_name ||
        participantById.get(id)?.display_name ||
        [doc.assignee, doc.controller, doc.owner].find((person) => person?.id === id)?.display_name ||
        (participants.isPending || participants.isError ? "…" : id);
  const members = [...participantById.keys()];
  const delegableMembers = delegation.isLoading
    ? []
    : delegation.data === undefined || delegation.data.unrestricted
      ? members
      : members.filter((id) => delegation.data.delegable_ids.includes(id));
  const candidates = [...candidateById.keys()];
  const recipientCandidates = candidates.filter(
    (id) => !recipientIds.includes(id),
  );
  const mentionPeople = [...participantById.entries()]
    .filter(([id]) => id !== currentUser.data?.id)
    .map(([id, person]) => ({
      id,
      displayName: person.display_name,
      email: person.email,
    }));
  const normalizedMentionSearch = mentionSearch
    .trim()
    .toLocaleLowerCase("ru-RU");
  const mentionCandidates = mentionPeople.filter((person) =>
    `${person.displayName} ${person.email ?? ""}`
      .toLocaleLowerCase("ru-RU")
      .includes(normalizedMentionSearch),
  );
  const personInput = (
    label: string,
    value: string,
    change: (value: string) => void,
    fieldName: string,
    candidates: readonly string[] = members,
  ) => (
    <label className="field">
      <span>{label}</span>
      <Select aria-label={label} required value={value} onChange={(e) => change(e.target.value)}>
        <option value="">Выберите сотрудника</option>
        {value && !candidates.includes(value) ? (
          <option value={value}>{name(value)}</option>
        ) : null}
        {candidates.map((id) => (
          <option key={id} value={id}>
            {name(id)}{candidateById.get(id)?.substituted_by ? ` · Отсутствует · замещает ${candidateById.get(id)!.substituted_by!.display_name}` : ""}
          </option>
        ))}
      </Select>
      {candidateById.get(value)?.substituted_by ? <small>Назначение получит {candidateById.get(value)!.substituted_by!.display_name}</small> : null}
      {field(fieldName)}
    </label>
  );
  // Автор версии и связавший файл названы именем, а не идентификатором.
  const authorName = (id: string | null) =>
    id === null
      ? "автор не указан"
      : (candidateById.get(id)?.display_name ??
        participantById.get(id)?.display_name ??
        "сотрудник вне списка участников");
  /** Перейти к источнику файла: письму или сообщению обсуждения. */
  const showSource = (target: "mail" | "discussion", id: string) => {
    setFocusId(id);
    setTab(target);
    onTabChange?.(target, id);
    mutation.reset();
  };
  // Вложения обсуждения: файл и сообщение, из которого он пришёл.
  const discussionAttachmentIds = [
    ...new Set(
      (comments.data ?? [])
        .filter((item) => !item.deleted)
        .flatMap((item) => item.attachment_file_ids),
    ),
  ];
  // Сервер отдаёт обсуждение от свежего к старому, так его и показываем: новая
  // реплика лежит сразу под полем ответа, а разговор уходит вниз, в прошлое.
  const conversation = comments.data ?? [];
  // Граница новых сообщений берётся один раз при открытии обсуждения. Если
  // пересчитывать её на каждую отметку прочтения, линия «Новые сообщения»
  // уезжала бы вниз прямо под курсором и человек терял бы место, где остановился.
  // Якорь — самое старое из непрочитанных, а лента идёт от новых: непрочитанные
  // лежат выше него, то есть под индексами не больше этого.
  const firstUnreadIndex = unreadAnchor
    ? conversation.findIndex((item) => item.id === unreadAnchor)
    : -1;
  const isUnreadAt = (index: number) =>
    firstUnreadIndex >= 0 && index <= firstUnreadIndex;
  const discussionFiles = discussionAttachmentIds.flatMap((fileId) => {
    const comment = (comments.data ?? []).find(
      (item) => !item.deleted && item.attachment_file_ids.includes(fileId),
    );

    return comment ? [{ fileId, commentId: comment.id }] : [];
  });
  useEffect(() => {
    const previous = window.document.activeElement;
    dialog.current?.focus();
    return () => {
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  useEffect(() => {
    window.document.body.classList.add("document-card-open");
    return () => window.document.body.classList.remove("document-card-open");
  }, []);
  /**
   * Обсуждение живое: сообщение коллеги появляется само.
   *
   * Сервер присылает в канал документа только повод перечитать ленту — сам
   * текст приходит обычным запросом, поэтому человек видит ровно то, что ему
   * положено. Если сокеты недоступны, всё продолжает работать по старому:
   * лента обновится при следующем действии.
   */
  useEffect(() => {
    if (tab !== "approval" && (tab !== "discussion" || !can("view_discussion"))) return;
    if (
      services.notifications === undefined ||
      services.apiClient === undefined
    )
      return;

    const notifications = services.notifications;
    const apiClient = services.apiClient;
    const refreshDurableDiscussion = () => {
      void Promise.all([
        client.invalidateQueries({ queryKey: [...key, "comments"] }),
        client.invalidateQueries({ queryKey: ["document-workflow"] }),
        client.invalidateQueries({ queryKey: ["notifications"] }),
      ]);
    };

    return openRealtimeSubscription({
      client: apiClient,
      loadConfig: () => notifications.realtimeConfig(),
      onReconnect: refreshDurableDiscussion,
      subscribe: (manager) =>
        manager.subscribeToDocument(doc.id, () => {
          setHasLiveUpdate(true);
          refreshDurableDiscussion();
        }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ключ запроса собирается из тех же данных.
  }, [doc.id, tab, services.apiClient, services.notifications, client]);
  /**
   * Первый заход в обсуждение: к нужному сообщению прокручиваем один раз.
   *
   * Лента обновляется после каждой отметки прочтения, и повторная прокрутка
   * к «первому непрочитанному» вырывала бы читателя из места, где он стоит.
   */
  const scrolledKey = useRef<string | null>(null);
  useEffect(() => {
    if (tab !== "discussion") {
      scrolledKey.current = null;
      return;
    }
    if (!comments.data) return;
    const scrollKey = `${doc.id}|${focusId ?? ""}`;
    if (scrolledKey.current === scrollKey) return;
    scrolledKey.current = scrollKey;
    // Без явной цели остаёмся наверху: новые сообщения лежат прямо под полем
    // ответа, и прокрутка к самому старому из них пропустила бы более свежие.
    const commentId = focusId;
    if (!commentId) return;
    const item = window.document.getElementById(`comment-${commentId}`);
    item?.scrollIntoView?.({ block: "center" });
    item?.focus({ preventScroll: true });
  }, [comments.data, doc.id, doc.first_unread_comment_id, tab, focusId]);
  /**
   * Открытая вкладка всегда видна в полосе разделов.
   *
   * На узком экране разделы не помещаются в строку и прокручиваются. Без
   * подсказки открытый раздел мог оказаться за краем экрана, и человек не
   * понимал, где находится.
   */
  useEffect(() => {
    tabsNav.current
      ?.querySelector<HTMLElement>("[aria-current]")
      ?.scrollIntoView?.({ inline: "center", block: "nearest" });
  }, [tab]);
  /**
   * Линия «Новые сообщения» ставится один раз на вход в обсуждение.
   *
   * Уход на другую вкладку считается концом чтения: вернувшись, человек видит
   * границу уже по новому состоянию, а не по той, что была полчаса назад.
   */
  useEffect(() => {
    if (tab !== "discussion") {
      setUnreadAnchor(null);
      reportedRead.current = null;
      return;
    }
    setUnreadAnchor(
      (current) => current ?? doc.first_unread_comment_id ?? null,
    );
  }, [tab, doc.first_unread_comment_id]);
  /**
   * Сообщение считается прочитанным, когда человек его долистал и увидел.
   *
   * Наблюдатель следит за непрочитанными сообщениями ленты; как только одно из
   * них побыло на экране, курсор прочтения двигается до самого нижнего
   * увиденного и уходит на сервер одним запросом. Так же ведут себя ленты, к
   * которым люди привыкли, и отдельная кнопка «прочитано» больше не нужна.
   *
   * Отметка идёт только вперёд: сервер не двигает курсор назад, а здесь ещё и
   * не повторяет уже отправленное сообщение.
   */
  useEffect(() => {
    if (tab !== "discussion" || firstUnreadIndex < 0) return;
    const list = conversationList.current;
    if (list === null || typeof IntersectionObserver === "undefined") return;

    // Сервер двигает курсор вперёд по времени: отметить новое сообщение, не
    // увидев более старые, нельзя. Поэтому курсор идёт от самого старого
    // непрочитанного вверх, пока каждое сообщение уже было на экране.
    const unreadOldestFirst = conversation
      .slice(0, firstUnreadIndex + 1)
      .reverse();
    const seen = new Set<string>();
    let timer: number | undefined;
    const report = () => {
      let furthest: string | null = null;
      for (const item of unreadOldestFirst) {
        if (!seen.has(item.id)) break;
        furthest = item.id;
      }
      if (furthest === null || furthest === reportedRead.current) return;
      reportedRead.current = furthest;
      markRead.mutate(furthest);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.commentId;
          if (!entry.isIntersecting || id === undefined) continue;
          seen.add(id);
          observer.unobserve(entry.target);
        }
        if (seen.size === 0) return;
        window.clearTimeout(timer);
        // Быстрая прокрутка мимо не считается прочтением, а пачка сообщений,
        // попавших на экран разом, уходит одним запросом.
        timer = window.setTimeout(report, 700);
      },
      // Без явного root видимость считается по окну и учитывает обрезку всеми
      // предками: на широком экране лента прокручивается внутри карточки, на
      // узком — вместе со страницей, и обе прокрутки работают одинаково.
      { threshold: 0.75 },
    );
    for (const item of list.querySelectorAll<HTMLElement>("[data-unread]"))
      observer.observe(item);

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- наблюдение пересобирается при смене ленты, а не на каждый рендер.
  }, [tab, firstUnreadIndex, comments.data]);
  function keyboard(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === "Escape" && !pending) {
      if (confirm) setConfirm(null);
      else onClose();
    }
    if (event.key !== "Tab") return;
    const elements = dialog.current?.querySelectorAll<HTMLElement>(
      "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]",
    );
    if (!elements?.length) return;
    const first = elements[0],
      last = elements[elements.length - 1];
    if (
      event.shiftKey &&
      (window.document.activeElement === first ||
        window.document.activeElement === dialog.current)
    ) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && window.document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
  const filesPanel = (
    <DocumentAttachments
      documentId={doc.id}
      accessScopeId={doc.access_scope_id}
      canUpload={can("manage_files")}
      canUnlink={can("link_files")}
      discussionFiles={discussionFiles}
      discussionFilesPending={can("view_discussion") && comments.isPending}
      discussionFilesError={can("view_discussion") && comments.isError}
      canViewDiscussion={can("view_discussion")}
      canSign={can("sign")}
      authorName={authorName}
      onShowInMail={(id) => showSource("mail", id)}
      onShowInDiscussion={(id) => showSource("discussion", id)}
      services={services}
    />
  );
  return (
    <div className="dw-preview-backdrop" role="presentation">
      <aside
        ref={dialog}
        tabIndex={-1}
        onKeyDown={keyboard}
        className="dw-preview dw-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dw-card-title"
      >
        <header className="dw-card__header">
          <div className="dw-card__heading">
            <div className="dw-card__identity">
              <span
                className={`dw-card__status dw-card__status--${doc.status}`}
              >
                {statusLabels[doc.status]}
              </span>
              {services.approvals ? <ApprovalStatusBadge services={services} documentId={doc.id} /> : null}
              <span>
                {doc.type === "incoming"
                  ? "Входящий"
                  : doc.type === "outgoing"
                    ? "Исходящий"
                    : "Внутренний"}{" "}
                документ
              </span>
            </div>
            <h2 id="dw-card-title">{doc.subject}</h2>
            <p className="dw-card__number">
              {doc.registration?.number ??
                doc.incoming_number ??
                "Без регистрационного номера"}
            </p>
          </div>
          <CloseButton onClick={onClose} />
        </header>
        <div className="dw-card__summary" aria-label="Сводка по документу">
          <div>
            <span>КПО</span>
            <strong>
              {scopes.data?.find((s) => s.id === doc.access_scope_id)?.name ??
                (scopes.isPending ? "…" : doc.access_scope_id)}
            </strong>
          </div>
          <div>
            <span>Исполнитель</span>
            <strong>{name(doc.assignee_id)}</strong>
            {doc.assignee?.redirected_from ? <small>вместо {doc.assignee.redirected_from.display_name}</small> : null}
          </div>
          <div>
            <span>Контроль</span>
            <strong>{name(doc.controller_id)}</strong>
            {doc.controller?.redirected_from ? <small>вместо {doc.controller.redirected_from.display_name}</small> : null}
            <small>до {date(doc.due_at)}</small>
          </div>
          <div>
            <span>Приоритет</span>
            <strong>{priorityLabels[doc.priority]}</strong>
          </div>
        </div>
        <details className="dw-card__visibility">
          <summary>
            Кто видит документ
            <span>
              {plural(
                (doc.recipient_ids ?? []).length,
                "получатель",
                "получателя",
                "получателей",
              )}{" "}
              ·{" "}
              {plural(
                doc.visibility_reasons?.length ?? 0,
                "основание",
                "основания",
                "оснований",
              )}{" "}
              доступа
            </span>
          </summary>
          <div>
            <p>
              <strong>Получатели:</strong>{" "}
              {(doc.recipient_ids ?? []).map(name).join(", ") || "не назначены"}
            </p>
            <p>
              <strong>Ваш доступ:</strong>{" "}
              {doc.visibility_reasons?.map((r) => reasons[r]).join(", ") ||
                "сервер не предоставил причину доступа"}
              .
            </p>
            {participants.isError ? (
              <Notice>
                Сведения об участниках: {errorText(participants.error)}
              </Notice>
            ) : null}
          </div>
        </details>
        <nav
          className="dw-card__tabs"
          aria-label="Разделы документа"
          ref={tabsNav}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-current={tab === t.id ? "page" : undefined}
              onClick={() => {
                setTab(t.id);
                onTabChange?.(t.id);
                mutation.reset();
              }}
            >
              <span>{t.label}</span>
              {t.id === "mail" && (doc.unread_mail_count ?? 0) > 0 ? (
                <strong aria-label={`Новых писем: ${doc.unread_mail_count}`}>
                  {doc.unread_mail_count}
                </strong>
              ) : null}
              {t.id === "discussion" && (doc.unread_comment_count ?? 0) > 0 ? (
                <strong
                  aria-label={`Новых внутренних сообщений: ${doc.unread_comment_count}`}
                >
                  {doc.unread_comment_count}
                </strong>
              ) : null}
            </button>
          ))}
        </nav>
        {mutation.isError ? (
          <div className="dw-card__notice" role="alert">
            <p>{errorText(mutation.error)}</p>
            {isValidationApiError(mutation.error)
              ? Object.entries(mutation.error.details.fields).map(
                  ([key, errors]) => (
                    <p key={key}>
                      {key}: {errors.join(" ")}
                    </p>
                  ),
                )
              : null}
          </div>
        ) : null}
        {tab === "overview" ? (
          <div className="dw-card__body">
            <DocumentOverview
              document={doc}
              services={services}
              canViewFiles={can("view_files")}
              canUpdate={can("update")}
              onOpenTab={(next) => {
                setTab(next);
                onTabChange?.(next);
              }}
            />
          </div>
        ) : null}
        {tab === "details" ? (
          <div className="dw-card__body">
            <SectionSwitcher
              items={detailSections}
              value={detailSection}
              ariaLabel="Данные и управление документом"
              onChange={(section) => {
                setDetailSection(section);
                mutation.reset();
              }}
            />
            <div className="dw-card__section-intro">
              <h3>
                {
                  detailSections.find((section) => section.id === detailSection)
                    ?.label
                }
              </h3>
              <p>
                {
                  detailSections.find((section) => section.id === detailSection)
                    ?.hint
                }
              </p>
            </div>
            {detailSection === "main" ? (
              <div className="dw-card__panel-grid">
                <form
                  className="dw-panel"
                  onSubmit={(e) => {
                    e.preventDefault();
                    run(() =>
                      api.updateDocument(doc.id, {
                        subject: subject.trim(),
                        description: description || null,
                        priority,
                        incoming_number: incoming || null,
                        document_date: documentDate || null,
                        copies_count: copies ? Number(copies) : null,
                        sheets_count: sheets.trim() || null,
                        received_at: new Date(received).toISOString(),
                      }),
                    );
                  }}
                >
                  <h3>Реквизиты</h3>
                  <fieldset disabled={!can("update") || pending}>
                    <label className="field">
                      <span>Тема</span>
                      <input
                        required
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                      {field("subject")}
                    </label>
                    <label className="field">
                      <span>Описание</span>
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                      {field("description")}
                    </label>
                    <label className="field">
                      <span>Приоритет</span>
                      <Select
                        value={priority}
                        onChange={(e) =>
                          setPriority(e.target.value as Priority)
                        }
                      >
                        {Object.entries(priorityLabels).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </Select>
                      {field("priority")}
                    </label>
                    <label className="field">
                      <span>Исходящий номер отправителя</span>
                      <input
                        value={incoming}
                        onChange={(e) => setIncoming(e.target.value)}
                      />
                      {field("incoming_number")}
                    </label>
                    <label className="field">
                      <span>Дата документа</span>
                      <input
                        type="date"
                        value={documentDate}
                        onChange={(e) => setDocumentDate(e.target.value)}
                      />
                      {field("document_date")}
                    </label>
                    {/* Колонки бумажного журнала: листы — текстом, в журнале
                        пишут «1 + приложение». */}
                    <label className="field">
                      <span>Количество экземпляров</span>
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={copies}
                        onChange={(e) => setCopies(e.target.value)}
                      />
                      {field("copies_count")}
                    </label>
                    <label className="field">
                      <span>Количество листов</span>
                      <input
                        maxLength={255}
                        placeholder="Например, 1 + приложение"
                        value={sheets}
                        onChange={(e) => setSheets(e.target.value)}
                      />
                      {field("sheets_count")}
                    </label>
                    <label className="field">
                      <span>Дата поступления</span>
                      <input
                        type="datetime-local"
                        required
                        value={received}
                        onChange={(e) => setReceived(e.target.value)}
                      />
                      {field("received_at")}
                    </label>
                    <button>Сохранить реквизиты</button>
                  </fieldset>
                  {!can("update") ? (
                    <p>Редактирование недоступно: недостаточно прав.</p>
                  ) : null}
                  <p>
                    Контрагент: {doc.counterparty?.name ?? "не определён"}.
                    Отправитель:{" "}
                    {typeof doc.sender_snapshot?.email === "string"
                      ? doc.sender_snapshot.email
                      : "не указан"}
                  </p>
                </form>
                <form
                  className="dw-panel"
                  onSubmit={(e) => {
                    e.preventDefault();
                    run(() =>
                      api.register(doc.id, {
                        ...(journal ? { journal } : {}),
                        ...(manualNumber.trim()
                          ? { number: manualNumber.trim() }
                          : {}),
                        registration_date: registrationDate,
                        incoming_number: incoming || null,
                        incoming_date: documentDate || null,
                      }),
                    );
                  }}
                >
                  <h3>Регистрация</h3>
                  <p>
                    Номер:{" "}
                    {doc.registration?.number ?? "будет присвоен журналом"}.
                    Дата: {doc.registration?.date ?? "не зарегистрирован"}
                  </p>
                  <fieldset disabled={!can("register") || pending}>
                    <label className="field">
                      <span>Дата регистрации</span>
                      <input
                        type="date"
                        required
                        value={registrationDate}
                        onChange={(e) => setRegistrationDate(e.target.value)}
                      />
                      {field("registration_date")}
                    </label>
                    {/* Пустое поле — номер выдаёт журнал по порядку. Вручную
                        указывают дополнение к номеру, например «14/1». */}
                    <label className="field">
                      <span>Номер (необязательно)</span>
                      <input
                        maxLength={40}
                        placeholder="Выдаст журнал, например № 219"
                        value={manualNumber}
                        onChange={(e) => setManualNumber(e.target.value)}
                      />
                      {field("number")}
                    </label>
                    <label className="field">
                      <span>Код журнала (необязательно)</span>
                      <input
                        pattern="[A-Za-z0-9_-]+"
                        value={journal}
                        onChange={(e) => setJournal(e.target.value)}
                      />
                      {field("journal")}
                    </label>
                    <button>Зарегистрировать</button>
                  </fieldset>
                </form>
              </div>
            ) : null}
            {detailSection === "execution" ? (
              <div className="dw-card__panel-grid">
                <section className="dw-panel">
                  <h3>Назначение и контроль</h3>
                  {participants.isError ? (
                    <p>
                      Список пользователей с доступом к документу недоступен.
                    </p>
                  ) : null}
                  <label className="field">
                    <span>Причина изменения</span>
                    <textarea
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                    {field("reason")}
                  </label>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(() =>
                        api.assign(doc.id, {
                          assignee_id: assignee,
                          reason: reason || null,
                        }),
                      );
                    }}
                  >
                    <fieldset
                      disabled={
                        !can("assign") || pending || participants.isPending
                      }
                    >
                      {personInput(
                        "Исполнитель",
                        assignee,
                        setAssignee,
                        "assignee_id",
                        delegableMembers,
                      )}
                      <button>Назначить исполнителя</button>
                    </fieldset>
                  </form>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      run(() =>
                        api.setControl(doc.id, {
                          controller_id: controller,
                          due_at: new Date(due).toISOString(),
                          priority,
                          reason: reason || null,
                        }),
                      );
                    }}
                  >
                    <fieldset
                      disabled={
                        !can("control") || pending || participants.isPending
                      }
                    >
                      {personInput(
                        "Контролёр",
                        controller,
                        setController,
                        "controller_id",
                        delegableMembers,
                      )}
                      <label className="field">
                        <span>Срок (местное время)</span>
                        <input
                          type="datetime-local"
                          required
                          value={due}
                          onChange={(e) => setDue(e.target.value)}
                        />
                        {field("due_at")}
                      </label>
                      <button>Сохранить контроль</button>
                    </fieldset>
                  </form>
                </section>
                <Section title="История назначений" query={assignments}>
                  {assignments.data?.length ? (
                    <ul className="dw-relations">
                      {assignments.data.map((a) => (
                        <li key={a.id}>
                          {name(a.assignee_id)} — назначил {name(a.assigned_by)}
                          , {date(a.assigned_at)}; {a.reason ?? "без причины"};{" "}
                          {a.ended_at
                            ? `снят ${date(a.ended_at)}`
                            : "действующее назначение"}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Назначений пока нет.</p>
                  )}
                </Section>
                <Section title="Прочитали документ" query={reads}>
                  {reads.data?.length ? (
                    <ul>
                      {reads.data.map((r) => (
                        <li key={r.id}>
                          {r.user?.display_name ?? name(r.user_id)} —{" "}
                          {date(r.read_at)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Документ пока никто не отметил прочитанным.</p>
                  )}
                  <button
                    disabled={pending || !doc.unread}
                    onClick={() => run(() => api.markRead(doc.id))}
                  >
                    Отметить документ прочитанным
                  </button>
                </Section>
              </div>
            ) : null}
            {detailSection === "access" ? (
              <div className="dw-card__panel-grid">
                <Section title="Участники и получатели" query={participants}>
                  <p className="dw-access-intro">
                    Получатели — официальные адресаты документа. Участники
                    получают доступ для совместной работы, но не становятся
                    адресатами.
                  </p>
                  <ul className="dw-access-people">
                    {people.map((p, i) => (
                      <li key={p.id ?? `${p.role}-${i}`}>
                        <span>
                          <strong>{name(p.user_id)}</strong>
                          <small>{roleLabels[p.role]}</small>
                        </span>
                        {p.can_remove ? (
                          <button
                            type="button"
                            className="button button--text dw-access-people__remove"
                            aria-label={`Удалить ${removableRoleLabels[p.role] ?? "сотрудника"} ${name(p.user_id)}`}
                            disabled={pending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Удалить ${name(p.user_id)} из роли «${roleLabels[p.role]}»? Другие основания доступа сохранятся.`,
                                )
                              ) {
                                if (p.role === "recipient") {
                                  setRecipientIds((ids) =>
                                    ids.filter((id) => id !== p.user_id),
                                  );
                                }
                                run(() =>
                                  p.role === "assignee"
                                    ? api.removeAssignment(doc.id)
                                    : p.role === "controller"
                                      ? api.removeControl(doc.id)
                                      : api.removeParticipant(doc.id, p.id!),
                                );
                              }
                            }}
                          >
                            Удалить
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {can("manage_participants") ? (
                    <>
                      {participantCandidates.isError ? (
                        <Notice>
                          Справочник пользователей для предоставления доступа:{" "}
                          {errorText(participantCandidates.error)}
                        </Notice>
                      ) : null}
                      <div className="dw-access-actions">
                        <form
                          className="dw-access-role"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const nextRecipients = [
                              ...recipientIds,
                              recipientId,
                            ];
                            setRecipientIds(nextRecipients);
                            setRecipientId("");
                            run(() =>
                              api.replaceRecipients(doc.id, nextRecipients),
                            );
                          }}
                        >
                          <fieldset
                            disabled={
                              pending ||
                              participantCandidates.isPending ||
                              participantCandidates.isError
                            }
                          >
                            {personInput(
                              "Добавить получателя",
                              recipientId,
                              setRecipientId,
                              "recipient_ids",
                              recipientCandidates,
                            )}
                            <small className="dw-access-role__hint">
                              Получатель будет указан как адресат и получит
                              доступ к документу.
                            </small>
                            <button>Добавить получателя</button>
                          </fieldset>
                        </form>
                        <form
                          className="dw-access-role"
                          onSubmit={(e) => {
                            e.preventDefault();
                            const selectedParticipantId = participantId;
                            setParticipantId("");
                            run(() =>
                              api.addParticipant(doc.id, selectedParticipantId),
                            );
                          }}
                        >
                          <fieldset disabled={pending}>
                            {personInput(
                              "Добавить участника",
                              participantId,
                              setParticipantId,
                              "user_id",
                              candidates,
                            )}
                            <small className="dw-access-role__hint">
                              Участник сможет работать с документом, но не будет
                              указан как адресат.
                            </small>
                            <button>Добавить участника</button>
                          </fieldset>
                        </form>
                      </div>
                    </>
                  ) : (
                    <p>Изменение участников недоступно: недостаточно прав.</p>
                  )}
                </Section>
                <Section title="Доступ между КПО" query={scopes}>
                  <ul>
                    {scopes.data?.map((s) => (
                      <li key={s.id}>
                        {s.name}: {s.access_level}
                        {s.id !== doc.access_scope_id &&
                        can("manage_scopes") ? (
                          <button
                            disabled={pending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Отозвать доступ КПО «${s.name}»? Другие основания доступа сохраняются.`,
                                )
                              )
                                run(() => api.revokeScope(doc.id, s.id));
                            }}
                          >
                            Отозвать доступ
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {can("manage_scopes") ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        run(() => api.shareScope(doc.id, scopeId, level));
                      }}
                    >
                      <fieldset disabled={pending}>
                        <label className="field">
                          <span>КПО для доступа</span>
                          <Select
                            required
                            value={scopeId}
                            onChange={(e) => setScopeId(e.target.value)}
                          >
                            <option value="">Выберите КПО</option>
                            {allScopes.data?.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </Select>
                          {field("scope")}
                        </label>
                        {allScopes.isError ? (
                          <Notice>{errorText(allScopes.error)}</Notice>
                        ) : null}
                        <label className="field">
                          <span>Уровень доступа</span>
                          <Select
                            value={level}
                            onChange={(e) =>
                              setLevel(e.target.value as typeof level)
                            }
                          >
                            <option value="read">Чтение</option>
                            <option value="write">Изменение</option>
                            <option value="manage">Управление</option>
                          </Select>
                          {field("access_level")}
                        </label>
                        <p>
                          После сохранения документ увидят сотрудники КПО «
                          {allScopes.data?.find((s) => s.id === scopeId)
                            ?.name ?? "не выбрано"}
                          » с разрешением на просмотр. Уровень: {level}.
                          Остальные основания доступа сохраняются.
                        </p>
                        <button>Сохранить доступ</button>
                      </fieldset>
                    </form>
                  ) : (
                    <p>Изменение доступа недоступно: недостаточно прав.</p>
                  )}
                </Section>
              </div>
            ) : null}
            {detailSection === "history" ? (
              <div className="dw-card__panel-grid">
                <Section title="Связанные документы" query={relations}>
                  {relations.data?.length ? (
                    <ul>
                      {relations.data.map((r) => (
                        <li className="dw-relation" key={r.id}>
                          <span className="dw-relation__document">
                            <a
                              href={`?document_id=${encodeURIComponent(r.related_document_id)}`}
                            >
                              {r.related_document_subject}
                            </a>
                            {r.related_document_number ? (
                              <small>{r.related_document_number}</small>
                            ) : null}
                          </span>
                          <small className="dw-relation__type">
                            {relationLabels[r.type]}
                            {r.note ? ` · ${r.note}` : ""}
                          </small>
                          {can("update") ? (
                            <button
                              disabled={pending}
                              onClick={() => {
                                if (window.confirm("Удалить связь документов?"))
                                  run(() => api.deleteRelation(r.id));
                              }}
                            >
                              Удалить связь
                            </button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Связанных документов нет.</p>
                  )}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!relatedId) return;
                      run(async () => {
                        await api.createRelation(doc.id, {
                          related_document_id: relatedId,
                          type: relationType,
                        });
                        setRelatedId("");
                        setRelationSearch("");
                      });
                    }}
                  >
                    <fieldset disabled={!can("update") || pending}>
                      <label className="field">
                        <span>Связанный документ</span>
                        <input
                          required
                          role="combobox"
                          aria-autocomplete="list"
                          aria-controls={relationListId}
                          aria-expanded={relationSearchOpen}
                          autoComplete="off"
                          placeholder="Название или номер, например MEMO-2026-00001"
                          value={relationSearch}
                          onFocus={() => setRelationSearchOpen(true)}
                          onBlur={() =>
                            window.setTimeout(
                              () => setRelationSearchOpen(false),
                              100,
                            )
                          }
                          onChange={(e) => {
                            setRelationSearch(e.target.value);
                            setRelatedId("");
                            setRelationSearchOpen(true);
                          }}
                        />
                        <small className="dw-relation-search__hint">
                          Начните вводить название или регистрационный номер.
                        </small>
                        {field("related_document_id")}
                      </label>
                      {relationSearchOpen &&
                      relationSearch.trim().length >= 2 ? (
                        <div
                          id={relationListId}
                          className="dw-relation-search__results"
                          role="listbox"
                          aria-label="Подходящие документы"
                        >
                          {relationCandidates.isPending ? (
                            <Notice role="status">Ищем документы…</Notice>
                          ) : relationCandidates.isError ? (
                            <Notice>
                              Не удалось выполнить поиск. Повторите попытку.
                            </Notice>
                          ) : relationCandidates.data?.data.filter(
                              (candidate) => candidate.id !== doc.id,
                            ).length ? (
                            relationCandidates.data.data
                              .filter((candidate) => candidate.id !== doc.id)
                              .map((candidate) => {
                                const number =
                                  candidate.registration?.number ??
                                  candidate.incoming_number ??
                                  "Без номера";
                                return (
                                  <button
                                    key={candidate.id}
                                    type="button"
                                    role="option"
                                    aria-selected={candidate.id === relatedId}
                                    onMouseDown={(event) =>
                                      event.preventDefault()
                                    }
                                    onClick={() => {
                                      setRelatedId(candidate.id);
                                      setRelationSearch(
                                        `${number} — ${candidate.subject}`,
                                      );
                                      setRelationSearchOpen(false);
                                    }}
                                  >
                                    <strong>{candidate.subject}</strong>
                                    <span>{number}</span>
                                  </button>
                                );
                              })
                          ) : (
                            <p>Подходящих документов не найдено.</p>
                          )}
                        </div>
                      ) : null}
                      <label className="field">
                        <span>Тип связи</span>
                        <Select
                          value={relationType}
                          onChange={(e) =>
                            setRelationType(
                              e.target.value as typeof relationType,
                            )
                          }
                        >
                          {Object.entries(relationLabels).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </Select>
                        {field("type")}
                      </label>
                      <button disabled={!relatedId}>Добавить связь</button>
                    </fieldset>
                  </form>
                </Section>
                <section className="dw-panel">
                  <h3>Экспорт истории</h3>
                  {can("export_history") ? (
                    <>
                      <label className="field">
                        <span>Формат</span>
                        <Select
                          value={exportFormat}
                          onChange={(e) =>
                            setExportFormat(
                              e.target.value as typeof exportFormat,
                            )
                          }
                        >
                          <option>json</option>
                          <option>csv</option>
                        </Select>
                        {field("format")}
                      </label>
                      <button
                        disabled={pending}
                        onClick={() =>
                          run(async () => {
                            const result = await api.requestHistoryExport(
                              doc.id,
                              exportFormat,
                            );
                            client.setQueryData(
                              [...key, "export", result.id],
                              result,
                            );
                            setExportId(result.id);
                          })
                        }
                      >
                        Создать экспорт
                      </button>
                    </>
                  ) : (
                    <p>
                      Экспорт недоступен: необходимы права чтения и экспорта
                      истории.
                    </p>
                  )}
                  {exportId ? (
                    <Section title="Состояние экспорта" query={exported}>
                      <Notice role="status">
                        {
                          {
                            queued: "В очереди",
                            processing: "Обрабатывается",
                            completed: "Готов",
                            failed: "Ошибка экспорта",
                          }[exported.data?.status ?? "queued"]
                        }
                      </Notice>
                      {exported.data?.status === "failed" ? (
                        <Notice>
                          Экспорт не выполнен. Создайте новую выгрузку. Если
                          ошибка повторится, сообщите администратору.
                        </Notice>
                      ) : null}
                      <button
                        disabled={
                          exported.data?.status !== "completed" ||
                          pending ||
                          !can("export_history")
                        }
                        onClick={() =>
                          run(async () => {
                            const result =
                              await api.historyExportDownload(exportId);
                            const link = window.document.createElement("a");
                            link.href = result.url;
                            link.rel = "noopener";
                            link.download = result.filename ?? "history";
                            link.click();
                          })
                        }
                      >
                        Скачать экспорт
                      </button>
                    </Section>
                  ) : null}
                </section>
              </div>
            ) : null}
          </div>
        ) : null}
        {tab === "mail" ? (
          <div className="dw-card__body">
            <DocumentMailThread
              key={`${doc.id}-${focusId}`}
              documentId={doc.id}
              accessScopeId={doc.access_scope_id}
              services={services}
              initialMessageId={
                focusId || doc.first_unread_mail_id || undefined
              }
              unreadCount={doc.unread_mail_count}
              canReply={can("reply")}
              canManageThreads={can("update")}
              canViewFiles={can("view_files")}
              canLinkFiles={can("link_files")}
            />
          </div>
        ) : null}
        {tab === "discussion" ? (
          <div className="dw-card__body">
            {can("view_discussion") ? (
              <Section
                title="Внутреннее обсуждение"
                query={comments}
                className="dw-discussion-panel"
              >
                <div className="dw-discussion__channel">
                  <div>
                    <strong>Только для сотрудников</strong>
                    <span>
                      Сообщения из этого раздела не отправляются контрагенту и
                      не попадают во внешнюю переписку.
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>Исполнитель</dt>
                      <dd>{name(doc.assignee_id)}</dd>
                    </div>
                    <div>
                      <dt>Участники</dt>
                      <dd>
                        <span className="dw-discussion__people">
                          {[...participantById.keys()].length ? (
                            [...participantById.keys()].map((id) => (
                              <span key={id}>{name(id)}</span>
                            ))
                          ) : (
                            <span>Пока не добавлены</span>
                          )}
                        </span>
                      </dd>
                    </div>
                  </dl>
                </div>
                {hasLiveUpdate ? (
                  <button
                    type="button"
                    className="button button--secondary dw-discussion__live"
                    onClick={() => {
                      setHasLiveUpdate(false);
                      // Свежая реплика теперь в конце ленты, как в переписке.
                      const messages =
                        window.document.querySelectorAll<HTMLElement>(
                          ".dw-discussion__message",
                        );
                      const latest = messages[messages.length - 1];
                      latest?.scrollIntoView({ block: "center" });
                      latest?.focus({ preventScroll: true });
                    }}
                  >
                    В обсуждении появились новые сообщения
                  </button>
                ) : null}
                <form
                  className={`dw-discussion__composer${
                    draggingFiles ? " dw-discussion__composer--dragging" : ""
                  }`}
                  onPaste={(event) => {
                    // Текст вставляется как обычно: перехватываем только
                    // скопированные файлы и снимки экрана.
                    const { files, types } = event.clipboardData;
                    if (!files.length || types.includes("text/plain")) return;
                    if (!can("discuss") || discussion.isPending) return;
                    event.preventDefault();
                    commentPicker.current?.addFiles([...files]);
                  }}
                  onDragEnter={(event) => {
                    if (!event.dataTransfer.types.includes("Files")) return;
                    event.preventDefault();
                    dragDepth.current += 1;
                    setDraggingFiles(true);
                  }}
                  onDragOver={(event) => {
                    if (!event.dataTransfer.types.includes("Files")) return;
                    // Без этого браузер откроет файл в этой вкладке.
                    event.preventDefault();
                    event.dataTransfer.dropEffect =
                      can("discuss") && !discussion.isPending ? "copy" : "none";
                  }}
                  onDragLeave={(event) => {
                    if (!event.dataTransfer.types.includes("Files")) return;
                    dragDepth.current = Math.max(0, dragDepth.current - 1);
                    if (dragDepth.current === 0) setDraggingFiles(false);
                  }}
                  onDrop={(event) => {
                    if (!event.dataTransfer.types.includes("Files")) return;
                    event.preventDefault();
                    dragDepth.current = 0;
                    setDraggingFiles(false);
                    if (!can("discuss") || discussion.isPending) return;
                    commentPicker.current?.addFiles([
                      ...event.dataTransfer.files,
                    ]);
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    say(async () => {
                      await api.addComment(
                        doc.id,
                        comment.trim(),
                        replyToId,
                        mentionedUserIds,
                        commentAttachments.map((file) => file.id),
                      );
                      setComment("");
                      setReplyToId(null);
                      setMentionedUserIds([]);
                      setMentionSearch("");
                      setMentionOpen(false);
                      setCommentAttachments([]);
                    });
                  }}
                >
                  <fieldset disabled={!can("discuss") || discussion.isPending}>
                    {replyToId ? (
                      <div className="dw-discussion__reply-to">
                        {(() => {
                          const parent = comments.data?.find(
                            (c) => c.id === replyToId,
                          );
                          return (
                            <span>
                              <strong>
                                Ответ для{" "}
                                {parent?.author?.display_name ??
                                  name(parent?.author_id ?? null)}
                              </strong>
                              <small>
                                {parent?.deleted
                                  ? "Сообщение удалено"
                                  : parent?.text?.slice(0, 180) ||
                                    (parent?.attachment_file_ids.length
                                      ? "Сообщение с файлом"
                                      : "Сообщение недоступно")}
                              </small>
                            </span>
                          );
                        })()}
                        <button
                          type="button"
                          className="button button--text button--compact"
                          onClick={() => setReplyToId(null)}
                        >
                          Отменить
                        </button>
                      </div>
                    ) : null}
                    <label className="field">
                      <span>
                        {replyToId
                          ? "Ответ в обсуждение"
                          : "Внутреннее сообщение"}
                      </span>
                      <textarea
                        required={commentAttachments.length === 0}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        maxLength={100000}
                        onKeyDown={(event) => {
                          if (
                            (event.ctrlKey || event.metaKey) &&
                            event.key === "Enter"
                          ) {
                            event.preventDefault();
                            event.currentTarget.form?.requestSubmit();
                          }
                        }}
                        aria-describedby="discussion-send-hint"
                      />
                      {field("text")}
                    </label>
                    {mentionedUserIds.length ? (
                      <div
                        className="dw-discussion__mention-chips"
                        aria-label="Будут упомянуты"
                      >
                        {mentionedUserIds.map((id) => (
                          <span key={id}>
                            @{name(id)}
                            <CloseButton
                              size="sm"
                              tone="ghost"
                              label={`Убрать упоминание ${name(id)}`}
                              onClick={() =>
                                setMentionedUserIds((ids) =>
                                  ids.filter((candidate) => candidate !== id),
                                )
                              }
                            />
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className="dw-discussion__composer-tools">
                      <button
                        ref={mentionButton}
                        type="button"
                        className="button button--secondary button--compact dw-discussion__mention-trigger"
                        aria-expanded={mentionOpen}
                        aria-controls={mentionPanelId}
                        onClick={() => {
                          setMentionOpen((open) => !open);
                          if (mentionOpen) setMentionSearch("");
                        }}
                      >
                        <HiOutlineAtSymbol aria-hidden="true" />
                        {mentionedUserIds.length
                          ? `Упомянуть ещё · ${mentionedUserIds.length}`
                          : "Упомянуть сотрудника"}
                      </button>
                      <AttachmentPicker
                        compact
                        ref={commentPicker}
                        services={services}
                        attachments={commentAttachments}
                        onChange={setCommentAttachments}
                        accessScopeId={doc.access_scope_id}
                        disabled={discussion.isPending}
                      />
                    </div>
                    {mentionOpen ? (
                      <section
                        id={mentionPanelId}
                        className="dw-discussion__mention-panel"
                        aria-labelledby={`${mentionPanelId}-title`}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            event.preventDefault();
                            setMentionOpen(false);
                            setMentionSearch("");
                            mentionButton.current?.focus();
                          }
                        }}
                      >
                        <div className="dw-discussion__mention-head">
                          <span>
                            <strong id={`${mentionPanelId}-title`}>
                              Кого уведомить
                            </strong>
                            <small>
                              Только сотрудники, добавленные к этому документу
                            </small>
                          </span>
                          <button
                            type="button"
                            className="button button--text button--compact"
                            onClick={() => {
                              setMentionOpen(false);
                              setMentionSearch("");
                              mentionButton.current?.focus();
                            }}
                          >
                            Готово
                          </button>
                        </div>
                        <label className="field">
                          <span className="visually-hidden">
                            Поиск среди участников документа
                          </span>
                          <input
                            ref={mentionSearchInput}
                            type="search"
                            value={mentionSearch}
                            placeholder="Имя или рабочая почта"
                            onChange={(event) =>
                              setMentionSearch(event.target.value)
                            }
                          />
                        </label>
                        {participants.isPending || currentUser.isPending ? (
                          <Notice role="status">Загружаем участников…</Notice>
                        ) : participants.isError || currentUser.isError ? (
                          <Notice>
                            Не удалось загрузить участников документа.
                          </Notice>
                        ) : mentionCandidates.length ? (
                          <div
                            className="dw-discussion__mention-options"
                            role="group"
                            aria-label="Участники документа"
                          >
                            {mentionCandidates.map((person) => (
                              <label key={person.id}>
                                <input
                                  type="checkbox"
                                  checked={mentionedUserIds.includes(person.id)}
                                  onChange={(event) =>
                                    setMentionedUserIds((ids) =>
                                      event.target.checked
                                        ? [...ids, person.id]
                                        : ids.filter((id) => id !== person.id),
                                    )
                                  }
                                />
                                <span>
                                  <strong>{person.displayName}</strong>
                                  {person.email ? (
                                    <small>{person.email}</small>
                                  ) : null}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : mentionPeople.length ? (
                          <p className="dw-secondary">
                            Среди участников ничего не найдено.
                          </p>
                        ) : (
                          <p className="dw-secondary">
                            Некого упоминать. Сначала добавьте сотруднику доступ
                            к документу.
                          </p>
                        )}
                        {field("mentioned_user_ids")}
                      </section>
                    ) : null}
                    <div className="dw-discussion__send">
                      <small id="discussion-send-hint">
                        Ctrl/⌘ + Enter — отправить. Файлы можно вставить (Ctrl/⌘
                        + V) или перетащить сюда.
                      </small>
                      <button
                        // Файл сам по себе — сообщение: текст нужен, только пока
                        // вложений нет.
                        disabled={
                          (!comment.trim() &&
                            commentAttachments.length === 0) ||
                          discussion.isPending
                        }
                      >
                        {discussion.isPending
                          ? "Отправляем…"
                          : "Отправить в обсуждение"}
                      </button>
                    </div>
                  </fieldset>
                  {!can("discuss") ? (
                    <p>Отправка сообщений недоступна: недостаточно прав.</p>
                  ) : null}
                </form>
                {discussion.isError &&
                !isValidationApiError(discussion.error) ? (
                  <div className="dw-card__notice" role="alert">
                    <p>{errorText(discussion.error)}</p>
                  </div>
                ) : null}
                {conversation.length ? (
                  <ul className="dw-discussion" ref={conversationList}>
                    {conversation.map((c, index) => (
                      <li
                        className={`dw-discussion__message${
                          c.author_id === currentUser.data?.id
                            ? " dw-discussion__message--own"
                            : ""
                        }${
                          isUnreadAt(index)
                            ? " dw-discussion__message--unread"
                            : ""
                        }${
                          index === firstUnreadIndex
                            ? " dw-discussion__message--first-unread"
                            : ""
                        }`}
                        id={`comment-${c.id}`}
                        tabIndex={-1}
                        data-comment-id={c.id}
                        {...(isUnreadAt(index)
                          ? { "data-unread": "true" }
                          : {})}
                      >
                        <div className="dw-discussion__meta">
                          <span>
                            <strong>
                              {c.author?.display_name ?? name(c.author_id)}
                            </strong>
                            <time dateTime={c.created_at}>
                              {date(c.created_at)}
                            </time>
                            {c.edit_version > 1 && !c.deleted ? (
                              <small>(изменено)</small>
                            ) : null}
                          </span>
                          {!c.deleted && editingCommentId !== c.id ? (
                            <span className="dw-discussion__actions">
                              {can("discuss") ? (
                                <button
                                  type="button"
                                  aria-label={`Ответить ${name(c.author_id)}`}
                                  title="Ответить"
                                  disabled={pending}
                                  onClick={() => setReplyToId(c.id)}
                                >
                                  <HiOutlineArrowUturnLeft aria-hidden="true" />
                                </button>
                              ) : null}
                              {c.author_id === currentUser.data?.id ? (
                                <>
                                  <button
                                    type="button"
                                    aria-label="Редактировать сообщение"
                                    title="Редактировать"
                                    disabled={pending}
                                    onClick={() => {
                                      setEditingCommentId(c.id);
                                      setEditedComment(c.text ?? "");
                                      setEditedMentionIds(c.mentioned_user_ids);
                                    }}
                                  >
                                    <HiOutlinePencil aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="dw-discussion__delete"
                                    aria-label="Удалить сообщение"
                                    title="Удалить"
                                    disabled={pending}
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          "Удалить это сообщение? Отменить удаление не получится.",
                                        )
                                      )
                                        say(() => api.deleteComment(c.id));
                                    }}
                                  >
                                    <HiOutlineTrash aria-hidden="true" />
                                  </button>
                                </>
                              ) : null}
                            </span>
                          ) : null}
                        </div>
                        {c.reply_to_comment_id
                          ? (() => {
                              const parent = comments.data?.find(
                                (item) => item.id === c.reply_to_comment_id,
                              );
                              return (
                                <p className="dw-discussion__quote">
                                  <strong>
                                    {parent?.author?.display_name ??
                                      name(parent?.author_id ?? null)}
                                  </strong>
                                  <span>
                                    {parent?.deleted
                                      ? "сообщение удалено"
                                      : parent?.text ||
                                        (parent?.attachment_file_ids.length
                                          ? "сообщение с файлом"
                                          : "сообщение недоступно")}
                                  </span>
                                </p>
                              );
                            })()
                          : null}
                        {editingCommentId === c.id ? (
                          <form
                            className="dw-discussion__edit"
                            onSubmit={(event) => {
                              event.preventDefault();
                              say(async () => {
                                await api.updateComment(
                                  c.id,
                                  editedComment.trim(),
                                  editedMentionIds,
                                );
                                setEditingCommentId(null);
                                setEditedComment("");
                                setEditedMentionIds([]);
                              });
                            }}
                          >
                            <label className="field">
                              <span>Текст сообщения</span>
                              <textarea
                                required
                                autoFocus
                                value={editedComment}
                                onChange={(event) =>
                                  setEditedComment(event.target.value)
                                }
                              />
                              {field("text")}
                            </label>
                            <div className="dw-discussion__edit-actions">
                              <button
                                type="button"
                                disabled={pending}
                                onClick={() => {
                                  setEditingCommentId(null);
                                  setEditedComment("");
                                  setEditedMentionIds([]);
                                }}
                              >
                                Отмена
                              </button>
                              <button
                                type="submit"
                                disabled={pending || !editedComment.trim()}
                              >
                                Сохранить
                              </button>
                            </div>
                          </form>
                        ) : (
                          // Сообщение из одного файла: пустой абзац только
                          // добавил бы отступ над вложением.
                          (c.deleted || c.text) && (
                            <p
                              className={
                                c.deleted ? "dw-discussion__deleted" : undefined
                              }
                            >
                              {c.deleted ? "Сообщение удалено" : c.text}
                            </p>
                          )
                        )}
                        {!c.deleted && c.mentioned_user_ids.length ? (
                          <p className="dw-discussion__mentioned">
                            <HiOutlineAtSymbol aria-hidden="true" />
                            {c.mentioned_user_ids
                              .map((id) => name(id))
                              .join(", ")}
                          </p>
                        ) : null}
                        {!c.deleted && c.attachment_file_ids.length ? (
                          <ul
                            className="dw-discussion__attachments"
                            aria-label="Вложения сообщения"
                          >
                            {c.attachment_file_ids.map((fileId) => (
                              <StoredFileAttachment
                                key={fileId}
                                fileId={fileId}
                                services={services}
                              />
                            ))}
                          </ul>
                        ) : null}
                        {index === firstUnreadIndex ? (
                          <p className="dw-discussion__new-line">
                            <span>Выше — новые сообщения</span>
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>Внутренних сообщений пока нет.</p>
                )}
              </Section>
            ) : (
              <p>Обсуждение недоступно: недостаточно прав.</p>
            )}
          </div>
        ) : null}
        {tab === "files" ? (
          <div className="dw-card__body">
            {can("view_files") ? (
              filesPanel
            ) : (
              <p>Файлы недоступны: недостаточно прав.</p>
            )}
          </div>
        ) : null}
        {tab === "timeline" ? (
          <div className="dw-card__body">
            <Section title="История документа" query={timeline}>
              {timeline.data?.pages.flatMap((p) => p.data).length ? (
                <ol className="dw-timeline">
                  {timeline.data.pages
                    .flatMap((p) => p.data)
                    .map((e) => (
                      <li
                        className={`dw-timeline__event dw-timeline__event--${e.channel}`}
                        key={e.id}
                      >
                        <div className="dw-timeline__header">
                          <strong>
                            {approvalTimelineTitle(e.type, e.payload) ?? timelineEventLabels[e.type] ?? e.type}
                          </strong>
                          <span>
                            <small className="dw-timeline__channel">
                              {e.channel === "internal"
                                ? "Внутреннее"
                                : e.channel === "external"
                                  ? "Внешняя переписка"
                                  : "Системное"}
                            </small>
                            <time dateTime={e.occurred_at}>
                              {date(e.occurred_at)}
                            </time>
                            {" · "}
                            {e.actor.name ?? "Система"}
                          </span>
                        </div>
                        <dl className="dw-timeline__details">
                          {Object.entries(e.payload).map(([k, v]) => (
                            <div key={k}>
                              <dt>{timelineFieldLabels[k] ?? k}</dt>
                              <dd>{timelineValue(k, v)}</dd>
                            </div>
                          ))}
                        </dl>
                        {e.redacted_fields.length ? (
                          <p className="dw-timeline__redacted">
                            Часть сведений скрыта из-за ограничений доступа.
                          </p>
                        ) : null}
                      </li>
                    ))}
                </ol>
              ) : (
                <p>Событий пока нет.</p>
              )}
              {timeline.hasNextPage ? (
                <button
                  disabled={timeline.isFetchingNextPage}
                  onClick={() => void timeline.fetchNextPage()}
                >
                  Ещё события
                </button>
              ) : null}
            </Section>
          </div>
        ) : null}
        <footer className="dw-card__footer">
          {(Object.keys(actions) as Action[])
            .filter(
              (a) => can(a) || (a === "complete" && can("complete_override")),
            )
            .map((a) => (
              <button
                key={a}
                className={
                  a === "cancel"
                    ? "button button--text dw-card__action--danger"
                    : a === "start" || a === "complete"
                      ? "button button--primary"
                      : "button button--secondary"
                }
                disabled={pending}
                onClick={() => {
                  mutation.reset();
                  setReason("");
                  setOutcome("");
                  setOverride(!can("complete") && a === "complete");
                  setConfirm(a);
                }}
              >
                {actions[a]}
              </button>
            ))}
        </footer>
        {confirm ? (
          <section
            className="dw-card__confirm"
            role="alertdialog"
            aria-label="Подтверждение действия"
          >
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => {
                  await api.lifecycle(
                    doc.id,
                    confirm,
                    reason.trim(),
                    outcome.trim(),
                    override,
                  );
                  setConfirm(null);
                });
              }}
            >
              <h3>{actions[confirm]} документ?</h3>
              {confirm === "complete" ? (
                <>
                  <label className="field">
                    <span>Результат исполнения</span>
                    <textarea
                      required
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value)}
                    />
                    {field("outcome")}
                  </label>
                  {override ? (
                    <p>
                      Завершение чужого назначения по отдельному праву. Причина
                      будет записана в аудит.
                    </p>
                  ) : null}
                </>
              ) : null}
              <label className="field">
                <span>Причина</span>
                <textarea
                  required={
                    confirm === "cancel" || confirm === "reopen" || override
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                {field("reason")}
              </label>
              <div className="dw-card__confirm-actions">
                <button className="button button--primary" disabled={pending}>
                  Подтвердить
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={pending}
                  onClick={() => setConfirm(null)}
                >
                  Вернуться к карточке
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </aside>
    </div>
  );
}
